import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tenantKeys, invoiceKeys } from "@/lib/queryKeys";
import { importInvoicesXml } from "@/services/platformTenantsService";
import type { ImportCfeResult } from "@/services/invoiceService";

const SUCCESS_TOAST_OPTIONS = {
  style: { background: "#f0fdf4", color: "#14532d" } as const,
  iconTheme: { primary: "#22c55e", secondary: "#f0fdf4" } as const,
};

export interface ImportEmittedCfeDialogProps {
  tenantId: string;
  tenantName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  /** When provided (e.g. from Settings), use this instead of platform API. Invalidates current tenant + invoices. */
  importFn?: (files: File[]) => Promise<ImportCfeResult>;
}

export function ImportEmittedCfeDialog({
  tenantId,
  tenantName,
  open,
  onOpenChange,
  onSuccess,
  importFn,
}: ImportEmittedCfeDialogProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);

  const mutation = useMutation({
    mutationFn: (filesToUpload: File[]) =>
      importFn
        ? importFn(filesToUpload)
        : importInvoicesXml(tenantId, filesToUpload),
    onSuccess: (data) => {
      const hasErrors = data.errors.length > 0;
      const merged = data.merged ?? 0;
      if (hasErrors) {
        toast.success(
          t("tenants.page.importEmittedDialog.successWithErrors", {
            imported: data.imported,
            merged,
            skipped: data.skipped,
            count: data.errors.length,
          }),
          { ...SUCCESS_TOAST_OPTIONS }
        );
        data.errors.forEach((e) => {
          const msg =
            e.code === "wrong_type_use_received"
              ? t("tenants.page.importEmittedDialog.errorWrongTypeUseReceived")
              : e.message;
          toast.error(e.filename ? `${e.filename}: ${msg}` : msg, {
            duration: 5000,
          });
        });
      } else {
        let messageKey: string;
        if (merged > 0 && data.imported === 0 && data.skipped === 0) {
          messageKey = "tenants.page.importEmittedDialog.successMergedOnly";
        } else if (data.imported > 0 && merged === 0 && data.skipped === 0) {
          messageKey = "tenants.page.importEmittedDialog.successImportedOnly";
        } else if (data.imported === 0 && merged === 0 && data.skipped > 0) {
          messageKey = "tenants.page.importEmittedDialog.successAllSkipped";
        } else {
          messageKey = "tenants.page.importEmittedDialog.success";
        }
        toast.success(
          t(messageKey, {
            imported: data.imported,
            merged,
            skipped: data.skipped,
          }),
          { ...SUCCESS_TOAST_OPTIONS }
        );
      }
      setFiles([]);
      onOpenChange(false);
      if (importFn) {
        queryClient.invalidateQueries({ queryKey: tenantKeys.current() });
        queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      } else {
        queryClient.invalidateQueries({ queryKey: tenantKeys.overview(tenantId) });
      }
      onSuccess?.();
    },
    onError: () => {
      toast.error(t("tenants.page.importEmittedDialog.error"));
    },
  });

  const handleClose = (): void => {
    if (!mutation.isPending) {
      setFiles([]);
      onOpenChange(false);
    }
  };

  const handleSubmit = (): void => {
    if (files.length === 0) return;
    mutation.mutate(files);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("tenants.page.importEmittedDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("tenants.page.importEmittedDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="import-emitted-files">
              {t("tenants.page.importEmittedDialog.fileLabel")}
            </Label>
            <Input
              id="import-emitted-files"
              type="file"
              accept=".xml,.zip"
              multiple
              className="cursor-pointer"
              onChange={(e) => {
                const list = e.target.files;
                if (list) setFiles(Array.from(list));
              }}
              disabled={mutation.isPending}
            />
            {files.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{f.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 px-1"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, j) => j !== i))
                      }
                      disabled={mutation.isPending}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose()}
            disabled={mutation.isPending}
          >
            {t("tenants.page.importEmittedDialog.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={files.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("tenants.page.importEmittedDialog.submitting")}
              </>
            ) : (
              t("tenants.page.importEmittedDialog.submit")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
